import {filter, find} from 'lodash';
import {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ActionButton, ConfirmModal, CopyText, Icon, ToggleFormik, UserInitials} from 'components';
import {UserContext} from 'context';
import {TRANSLATION_KEYS} from 'locale';
import {DomainInfo, UserSummary} from 'types';
import {formatDate, getParticipantName} from 'utils/transform';

import {SECURITY_FIELDS} from '../../settings.constants';

import {AddItemModal} from './AddItemModal';
import {WhitelistGrid} from './WhitelistGrid';
import {WhitelistTable} from './WhitelistTable';
import {CSP_QUERY_KEY} from './constants';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SETTINGS.CSP_WHITELIST;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

type Props = {
    value: {
        enabled: boolean;
        token: string;
        list: DomainInfo[];
    };
    users: UserSummary[];
    className?: string;
    setFieldValue: (field: string, value: any) => void;
}

export const CspWhitelist: React.FC<Props> = ({value, users, className, setFieldValue}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);
    const [addModalProps, setAddModalProps] = useState<{
        open: boolean;
    }>({open: false});
    const [deleteModalProps, setDeleteModalProps] = useState<{
        open: boolean;
        index?: number;
    }>({open: false});

    const handleDelete = useCallback((index: number) => {
        setDeleteModalProps({
            open: true,
            index
        });
    }, []);

    const handleConfirmDelete = useCallback((confirm: boolean, index: number) => {
        if (confirm) {
            const newList = filter(value.list, (_, i) => i !== index);
            setFieldValue(SECURITY_FIELDS.CSP_WHITELIST_LIST, newList);
        }
        setDeleteModalProps({open: false});
    }, [value, setFieldValue]);

    const handleAdd = useCallback((confirm: boolean, info: DomainInfo) => {
        if (confirm && info) {
            const index = value.list?.length || 0;
            setFieldValue(`${SECURITY_FIELDS.CSP_WHITELIST_LIST}[${index}]`, info);
        }
        setAddModalProps({open: false});
    }, [value, setFieldValue]);

    const renderCreatedBy = useCallback((eid: number) => {
        const user = find(users, {eid});
        const userName = getParticipantName(user, t(T_KEYS.DELETED_USER));
        return (
            <div className='tw-flex tw-items-center'>
                <Icon name='user' className='md:tw-hidden tw-ml-2 tw-mr-4' />
                <UserInitials user={user} className='-md:tw-hidden tw-mr-2' />
                {userName}
            </div>
        );
    }, [users, t]);

    const renderCreatedAt = useCallback((date: Date) => formatDate(date, timeZone, dateFormat), [timeZone, dateFormat]);

    const cspUrl = useMemo(() => {
        const url = new URL(window.location.origin);
        url.search = new URLSearchParams({[CSP_QUERY_KEY]: value.token}).toString();
        return url.href;
    }, [value.token]);

    return (
        <div className={className}>
            <div className='tw-ts-h5'>{t(T_KEYS.TITLE)}</div>
            <div className='tw-flex tw-items-center tw-my-2'>
                <span className='tw-mr-1'>{`${t(T_KEYS.URL)}:`}</span>
                <CopyText text={cspUrl} labelPlacement='left' />
            </div>
            <div className='tw-flex tw-items-center tw-justify-between'>
                <ToggleFormik
                    className='tw-my-4'
                    name={SECURITY_FIELDS.CSP_WHITELIST_ENABLED}
                    label={t(COMMON_T_KEYS.ALL.ENABLED)}
                />
                <ActionButton
                    action='create'
                    onClick={() => setAddModalProps({open: true})}
                >
                    {t(T_KEYS.ADD_BUTTON)}
                </ActionButton>
            </div>

            <WhitelistTable
                infos={value.list}
                renderCreatedBy={renderCreatedBy}
                renderCreatedAt={renderCreatedAt}
                className='-md:tw-hidden'
                onDelete={handleDelete}
            />

            <WhitelistGrid
                infos={value.list}
                renderCreatedBy={renderCreatedBy}
                renderCreatedAt={renderCreatedAt}
                className='md:tw-hidden'
                onDelete={handleDelete}
            />

            <AddItemModal {...addModalProps} onClose={handleAdd} />
            <ConfirmModal
                open={deleteModalProps.open}
                title={t(T_KEYS.MODAL.DELETE)}
                onClose={(confirm) => handleConfirmDelete(confirm, deleteModalProps.index)}
            />
        </div>
    );
};